import { isMobileSize } from '../../../Common/CommonHooks';
import './index.scss';

export let originTagName = '.bottom-menu .AddToSlip';

let defaultBallBGColor = '';
let animationOriginDomRef = null;

/**
 * Update the selector and background color of the animation origin.
 */
export const updateOriginLocation = (selector, bgColor = '') => {
    originTagName = selector;
    defaultBallBGColor = bgColor;
};

/**
 * Set a React ref or DOM element as the fallback animation origin.
 */
export const setAnimationOriginDom = (domOrRef) => {
    animationOriginDomRef = domOrRef;
};

/**
 * Get the fallback origin DOM element.
 * Supports both React refs and native DOM elements.
 */
const getFallbackOriginDom = () => {
    if (!animationOriginDomRef) {
        return null;
    }

    if ('current' in animationOriginDomRef) {
        return animationOriginDomRef.current;
    }

    return animationOriginDomRef;
};

/**
 * Get the corresponding ball style based on the current page.
 */
const getBallBGColor = () => {
    const pathName = window.location.href.toLowerCase();

    if (pathName.includes('marksix')) {
        return 'MSCBall';
    }

    if (pathName.includes('football')) {
        return 'FTCBall';
    }

    if (pathName.includes('racing')) {
        return 'HRCBall';
    }

    return defaultBallBGColor;
};

/**
 * Calculate animation duration and cubic-bezier based on vertical distance.
 *
 * The duration increases slightly with distance, but is capped so that
 * long-distance animations can still finish quickly.
 */
const getAnimationConfig = (distanceY) => {
    const minDuration = 0.55;
    const maxDuration = 0.78;

    // Shorten the duration to make the ball fall slightly faster.
    const calculatedDuration = minDuration + distanceY * 0.0004;

    const duration = Math.min(
        maxDuration,
        Math.max(minDuration, calculatedDuration)
    );


    let bezier = '.08,-0.35,.99,.33';

    if (distanceY <= 100) {
        bezier = '0.11,-0.57, 0.99, 0.4';
    }

    return {
        duration: Math.round(duration * 100) / 100,
        bezier
    };
};

/**
 * Play the add-to-slip animation.
 *
 * Each call creates an independent animation element, so rapid continuous
 * clicks will not overwrite or remove other running animations.
 */
export const AddToSlipAnimationFn = (num = 1, onEnd) => {
    try {
        if (!isMobileSize) {
            return;
        }

        const rootDom = document.getElementById('root');
        const targetDom = document.querySelector('.bottom-menu #bottom-menu-Int_Betslip');

        const selectorOriginDom = originTagName ? document.querySelector(originTagName) : null;

        const originDom = selectorOriginDom || getFallbackOriginDom();

        if (!rootDom || !originDom || !targetDom) {
            return;
        }

        const originRect = originDom.getBoundingClientRect();
        const targetRect = targetDom.getBoundingClientRect();

        const startX = Math.ceil(originRect.left + originRect.width * 0.5 - 10);
        const startY = Math.ceil(originRect.top + originRect.height * 0.5 - 15);

        const endX = Math.ceil(targetRect.left + targetRect.width * 0.5 - 10);
        const endY = Math.ceil(targetRect.top + targetRect.height * 0.5 - 10);

        const distanceY = Math.abs(endY - startY);
        const { duration, bezier } = getAnimationConfig(distanceY);

        /*
         * Create a new DOM element for every animation.
         * Do not use querySelector('.parabolaBall'), because multiple
         * animations may exist at the same time.
         */
        const ball = document.createElement('div');

        ball.className = ['parabolaBall', getBallBGColor()].filter(Boolean).join(' ');

        ball.textContent = String(num);

        Object.assign(ball.style, {
            top: `${startY}px`,
            left: `${startX}px`,
            display: 'block',
            transform: 'scale(1)',
            transition: 'none',
            pointerEvents: 'none'
        });

        rootDom.insertBefore(ball, rootDom.firstChild);

        let animationFinished = false;
        let fallbackTimer = null;

        const finishAnimation = () => {
            if (animationFinished) {
                return;
            }

            animationFinished = true;

            if (fallbackTimer) {
                window.clearTimeout(fallbackTimer);
            }

            ball.removeEventListener('transitionend', handleTransitionEnd);

            ball.remove();

            if (typeof onEnd === 'function') {
                window.setTimeout(onEnd, 100);
            }
        };

        const handleTransitionEnd = (event) => {
            /*
             * The ball has multiple transition properties.
             * Use the top transition as the animation completion signal.
             */
            if (event.target === ball && event.propertyName === 'top') {
                finishAnimation();
            }
        };

        ball.addEventListener('transitionend', handleTransitionEnd);

        /*
         * Two requestAnimationFrame calls ensure that the browser paints
         * the initial position before applying the target position.
         */
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                if (!ball.isConnected) {
                    return;
                }

                ball.style.willChange = 'left, top, transform';

                ball.style.transition = [
                    `left ${duration}s linear`,
                    `top ${duration}s cubic-bezier(${bezier})`,
                    `transform ${duration}s ease-out`
                ].join(', ');

                ball.style.left = `${endX}px`;
                ball.style.top = `${endY}px`;
                ball.style.transform = 'scale(0.7)';
            });
        });

        /*
         * Fallback cleanup in case transitionend is not fired, for example
         * when the browser tab loses focus or styles are interrupted.
         */
        fallbackTimer = window.setTimeout(finishAnimation, duration * 1000 + 300);
    } catch (error) {
        console.error('AddToSlipAnimationFn failed:', error);
    }
};
